import { nextTick } from "vue";

// Single-page navigations don't move focus: screen-reader users hear nothing
// when the page changes, and keyboard users stay on the link they used. After
// each real page change, focus the new page's main heading (or its main
// landmark) without scrolling.
const TARGETS = ["#main h1", "#main h2", "h1", "#main"];

export function focusNewPage(doc = document) {
  // Leave focus alone while the user is inside a dialog or a sheet.
  if (doc.activeElement?.closest?.('[aria-modal="true"]')) return false;
  for (const selector of TARGETS) {
    for (const target of doc.querySelectorAll(selector)) {
      // An empty heading (still loading) has nothing to announce.
      if (target.matches("h1, h2") && !target.textContent.trim()) continue;
      if (!target.hasAttribute("tabindex")) {
        // Focusable from script only; no outline for this programmatic focus.
        target.setAttribute("tabindex", "-1");
        target.classList.add("route-focus-target");
      }
      target.focus({ preventScroll: true });
      // A hidden element can't take focus: try the next one.
      if (doc.activeElement === target) return true;
    }
  }
  return false;
}

// Real page changes only: not the first load, not a query- or hash-only
// change on the same page (tabs, filters), and not back/forward, where the
// browser restores the old scroll position and a heading at the top would
// leave keyboard focus off-screen.
export function shouldMoveFocus(to, from, failure, historyTraversal = false) {
  if (failure || historyTraversal) return false;
  if (!from || from.matched.length === 0) return false;
  return to.path !== from.path;
}

// Wires focusNewPage to the router. Back/forward shows up as a popstate
// event before the router's navigation for it finishes.
export function installRouteFocus(router, win = window) {
  let historyTraversal = false;
  win.addEventListener("popstate", () => {
    historyTraversal = true;
  });
  router.afterEach((to, from, failure) => {
    const traversal = historyTraversal;
    historyTraversal = false;
    if (shouldMoveFocus(to, from, failure, traversal)) {
      // Once the new page has rendered.
      nextTick(() => focusNewPage(win.document));
    }
  });
}
